import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import {
  Plus,
  Search,
  TrendingUp,
  Calendar,
  DollarSign,
  Tag,
  Edit,
  Trash2,
  X,
  FileText,
  Download,
  Printer,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const incomeCategories = [
  { value: 'service_revenue', label: 'Service Revenue' },
  { value: 'product_sales', label: 'Product Sales' },
  { value: 'consulting', label: 'Consulting Fees' },
  { value: 'subscription', label: 'Subscription Income' },
  { value: 'commission', label: 'Commission Income' },
  { value: 'interest', label: 'Interest Income' },
  { value: 'refund', label: 'Refunds Received' },
  { value: 'other', label: 'Other Income' },
];

const paymentMethods = [
  { value: 'bank_transfer', label: 'Bank Transfer (ACH)' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const IncomePage = () => {
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const reportRef = useRef(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: 'service_revenue',
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    client_name: '',
    invoice_number: '',
    notes: '',
  });

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - i).toString());
  
  const monthOptions = [
    { value: 'all', label: 'All Months' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  useEffect(() => {
    fetchIncome();
  }, []);

  const fetchIncome = async () => {
    try {
      const response = await axios.get(`${API}/income`);
      setIncome(response.data);
    } catch (error) {
      console.error('Failed to fetch income:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
    }
  };

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Income_Report_${yearFilter}_${monthFilter !== 'all' ? monthFilter : 'Full_Year'}`,
  });

  const getReportPeriodLabel = () => {
    if (yearFilter === 'all') return 'All Time';
    const monthName = monthOptions.find(m => m.value === monthFilter)?.label || '';
    if (monthFilter === 'all') return `Year ${yearFilter}`;
    return `${monthName} ${yearFilter}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      if (selectedIncome) {
        await axios.put(`${API}/income/${selectedIncome.id}`, payload);
      } else {
        await axios.post(`${API}/income`, payload);
      }
      fetchIncome();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save income:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this income record?')) return;
    try {
      await axios.delete(`${API}/income/${id}`);
      fetchIncome();
    } catch (error) {
      console.error('Failed to delete income:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: 'service_revenue',
      amount: '',
      payment_method: 'bank_transfer',
      reference_number: '',
      client_name: '',
      invoice_number: '',
      notes: '',
    });
    setSelectedIncome(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredIncome = income.filter(item => {
    const matchesSearch = 
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    
    // Year filter
    const itemDate = item.date || '';
    const itemYear = itemDate.substring(0, 4);
    const matchesYear = yearFilter === 'all' || itemYear === yearFilter;
    
    // Month filter
    const itemMonth = itemDate.substring(5, 7);
    const matchesMonth = monthFilter === 'all' || itemMonth === monthFilter;
    
    return matchesSearch && matchesCategory && matchesYear && matchesMonth;
  });

  const totalIncome = filteredIncome.reduce((sum, item) => sum + item.amount, 0);

  const incomeByCategory = incomeCategories.map(cat => ({
    ...cat,
    total: filteredIncome.filter(i => i.category === cat.value).reduce((sum, i) => sum + i.amount, 0),
  })).filter(cat => cat.total > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-white font-medium">Income</h1>
          <p className="text-gray-400">Track all your business income for tax purposes</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl transition-colors"
            data-testid="generate-report-btn"
          >
            <FileText className="w-5 h-5" />
            Generate Report
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Income
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-green-900/30 to-green-800/20 rounded-2xl border border-green-500/20 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">
              Total Income {yearFilter !== 'all' ? `(${yearFilter})` : '(All Time)'}
            </span>
          </div>
          <div className="text-3xl text-white font-bold">{formatCurrency(totalIncome)}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Transactions</span>
          </div>
          <div className="text-3xl text-white font-bold">{filteredIncome.length}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-gray-400 text-sm">Average</span>
          </div>
          <div className="text-3xl text-white font-bold">
            {formatCurrency(filteredIncome.length > 0 ? totalIncome / filteredIncome.length : 0)}
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {incomeByCategory.length > 0 && (
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <h3 className="text-white font-medium mb-4">Income by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {incomeByCategory.map((cat) => (
              <div key={cat.value} className="bg-[#0a0a0b] rounded-xl p-4">
                <p className="text-gray-400 text-sm mb-1">{cat.label}</p>
                <p className="text-white font-bold">{formatCurrency(cat.total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search income..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white"
          data-testid="year-filter"
        >
          <option value="all">All Years</option>
          {yearOptions.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
          className="px-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white"
          data-testid="month-filter"
        >
          {monthOptions.map((month) => (
            <option key={month.value} value={month.value}>{month.label}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white"
        >
          <option value="all">All Categories</option>
          {incomeCategories.map((cat) => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Income Table */}
      <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredIncome.length === 0 ? (
          <div className="text-center py-20">
            <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No income records found for selected period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Date</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Description</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Category</th>
                  <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Client</th>
                  <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Amount</th>
                  <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncome.map((item) => (
                  <tr key={item.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-gray-400">{formatDate(item.date)}</td>
                    <td className="px-6 py-4 text-white">{item.description}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                        {incomeCategories.find(c => c.value === item.category)?.label || item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400">{item.client_name || '-'}</td>
                    <td className="px-6 py-4 text-right text-green-400 font-medium">+{formatCurrency(item.amount)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedIncome(item);
                            setFormData({
                              ...item,
                              date: item.date?.split('T')[0] || item.date,
                            });
                            setShowModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-lg bg-[#1a1a1f] rounded-3xl border border-white/10 p-8">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl text-white font-medium mb-6">
              {selectedIncome ? 'Edit Income' : 'Add Income'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Amount *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Description *</label>
                <input
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  placeholder="e.g., Developer staffing services - January"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  >
                    {incomeCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Payment Method</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  >
                    {paymentMethods.map((pm) => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Client Name</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Invoice #</label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Reference/Check Number</label>
                <input
                  type="text"
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium transition-colors"
                >
                  {selectedIncome ? 'Update' : 'Add Income'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Income Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowReportModal(false)} />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl p-0 my-8 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl text-gray-800 font-semibold">Income Report - {getReportPeriodLabel()}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  data-testid="print-report-btn"
                >
                  <Printer className="w-4 h-4" />
                  Print / Save PDF
                </button>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Printable Report Content */}
            <div ref={reportRef} className="p-8" style={{ backgroundColor: 'white' }}>
              {/* Report Header */}
              <div style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '20px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                      {companySettings?.business_name || 'Business Income Report'}
                    </h1>
                    {companySettings?.address && (
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                        {companySettings.address}
                        {companySettings.city && `, ${companySettings.city}`}
                        {companySettings.state && `, ${companySettings.state}`}
                        {companySettings.zip && ` ${companySettings.zip}`}
                      </p>
                    )}
                    {companySettings?.ein && (
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                        EIN: {companySettings.ein}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed', margin: 0 }}>
                      INCOME REPORT
                    </h2>
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                      Period: {getReportPeriodLabel()}
                    </p>
                    <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                      Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#166534', fontSize: '12px', fontWeight: '500', margin: 0 }}>TOTAL INCOME</p>
                  <p style={{ color: '#166534', fontSize: '24px', fontWeight: 'bold', margin: '4px 0 0 0' }}>{formatCurrency(totalIncome)}</p>
                </div>
                <div style={{ backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#5b21b6', fontSize: '12px', fontWeight: '500', margin: 0 }}>TRANSACTIONS</p>
                  <p style={{ color: '#5b21b6', fontSize: '24px', fontWeight: 'bold', margin: '4px 0 0 0' }}>{filteredIncome.length}</p>
                </div>
                <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#1e40af', fontSize: '12px', fontWeight: '500', margin: 0 }}>AVERAGE</p>
                  <p style={{ color: '#1e40af', fontSize: '24px', fontWeight: 'bold', margin: '4px 0 0 0' }}>
                    {formatCurrency(filteredIncome.length > 0 ? totalIncome / filteredIncome.length : 0)}
                  </p>
                </div>
              </div>

              {/* Income by Category */}
              {incomeByCategory.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Income by Category
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                    {incomeByCategory.map((cat) => (
                      <div key={cat.value} style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
                        <p style={{ color: '#6b7280', fontSize: '12px', margin: 0 }}>{cat.label}</p>
                        <p style={{ color: '#111827', fontSize: '16px', fontWeight: '600', margin: '4px 0 0 0' }}>{formatCurrency(cat.total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Transaction List */}
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Transaction Details
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Description</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Client</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Category</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Invoice #</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIncome.map((item, index) => (
                      <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{formatDate(item.date)}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#111827' }}>{item.description}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{item.client_name || '-'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                          {incomeCategories.find(c => c.value === item.category)?.label || item.category}
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{item.invoice_number || '-'}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', color: '#059669', fontWeight: '600', textAlign: 'right' }}>
                          {formatCurrency(item.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f0fdf4' }}>
                      <td colSpan="5" style={{ padding: '12px', borderTop: '2px solid #e5e7eb', fontWeight: '600', color: '#166534', textAlign: 'right' }}>
                        TOTAL:
                      </td>
                      <td style={{ padding: '12px', borderTop: '2px solid #e5e7eb', fontWeight: 'bold', color: '#166534', textAlign: 'right', fontSize: '16px' }}>
                        {formatCurrency(totalIncome)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Footer */}
              <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
                <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0 }}>
                  This report was generated for tax and accounting purposes. Please retain for your records.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomePage;
